import { TournamentsAnalysisCards } from "@/components/general/TournamentAnalysis/TournamentAnalysisCards/TournamentAnalysisCards";
import { TournamentAnalysisChart } from "@/components/general/TournamentAnalysis/TournamentAnalysisChart";
import { TournamentAnalysisGamesSummary } from "@/components/general/TournamentAnalysis/TournamentAnalysisGamesSummary";
import {
    TournamentAnalysisHomeAwayPerformance,
    type StandingsSubTab,
} from "@/components/general/TournamentAnalysis/TournamentAnalysisHomeAwayPerformance";
import { TournamentAnalysisMetrics } from "@/components/general/TournamentAnalysis/TournamentAnalysisMetrics";
import { TournamentAnalysisAchievements } from "@/components/general/TournamentAnalysis/TournamentAnalysisAchievements/TournamentAnalysisAchievements";
import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";

import {
    tournamentSummaryQueryOptions,
    tournamentHomeAwayQueryOptions,
    tournamentTopPlayersQueryOptions,
    tournamentMatchesQueryOptions,
    teamStandingsByLocationQueryOptions,
} from "@/api/tournamentAnalysis";
import { quickStatsQueryOptions } from "@/api/quickStats";
import { useTournamentContext } from "../page";
import { useSelectedTeam } from "@/context/teamContext";
import type { Tournament } from "@/api/tournaments";

export const TournamentOverviewPage = () => {
    const { selectedTournament, selectedTournamentId, tournaments, matchXgs } = useTournamentContext();
    const { teamId } = useSelectedTeam();
    const isTournamentsLoading = tournaments === undefined || matchXgs === undefined;
    const selectedTournamentData = tournaments?.find(
        (tournament: Tournament) => tournament.name === selectedTournament
    );

    const [homeAwayActiveTab, setHomeAwayActiveTab] = useState<"standings" | "home-away">("standings");
    const [standingsSubTab, setStandingsSubTab] = useState<StandingsSubTab>("overall");
    const [chartSubTitle, setChartSubTitle] = useState<{ sub: string; subColor?: string }>({ sub: "" });
    const handleChartTitleChange = useCallback((t: { main: string; sub: string; subColor?: string }) => {
        setChartSubTitle({ sub: t.sub, subColor: t.subColor });
    }, []);

    const enabled = !!selectedTournamentId && selectedTournamentId > 0;

    const isPlayoffTournament =
        selectedTournament && (selectedTournament.endsWith("Playoffs") || selectedTournament.endsWith("Qualifiers"));

    useEffect(() => {
        setStandingsSubTab(isPlayoffTournament ? "playoffs" : "overall");
    }, [isPlayoffTournament]);

    const shouldFetchHomeAway = !isPlayoffTournament;

    const { data: summaryData, isLoading: summaryFetching } = useQuery({
        ...tournamentSummaryQueryOptions(selectedTournamentId || 0, teamId),
        enabled,
    });
    const summaryLoading = !enabled || summaryFetching;

    const { data: homeAwayData, isLoading: homeAwayFetching } = useQuery({
        ...tournamentHomeAwayQueryOptions(selectedTournamentId || 0, teamId),
        enabled,
    });
    const homeAwayLoading = !enabled || homeAwayFetching;

    const { data: quickStatsData, isLoading: quickStatsLoading } = useQuery({
        ...quickStatsQueryOptions(selectedTournamentId || 0, teamId),
        enabled,
    });

    const { data: topPlayersData, isLoading: topPlayersFetching } = useQuery({
        ...tournamentTopPlayersQueryOptions(selectedTournamentId || 0, teamId),
        enabled,
    });
    const topPlayersLoading = !enabled || topPlayersFetching;

    const { data: matchesData, isLoading: matchesFetching } = useQuery({
        ...tournamentMatchesQueryOptions(selectedTournamentId || 0, teamId),
        enabled,
    });
    const matchesLoading = !enabled || matchesFetching;

    const { data: overallStandings, isLoading: overallFetching } = useQuery({
        ...teamStandingsByLocationQueryOptions(selectedTournamentId || 0, "overall", teamId),
        enabled,
    });
    const { data: homeStandings, isLoading: homeFetching } = useQuery({
        ...teamStandingsByLocationQueryOptions(selectedTournamentId || 0, "home", teamId),
        enabled: enabled && shouldFetchHomeAway,
    });
    const { data: awayStandings, isLoading: awayFetching } = useQuery({
        ...teamStandingsByLocationQueryOptions(selectedTournamentId || 0, "away", teamId),
        enabled: enabled && shouldFetchHomeAway,
    });
    const standingsLoading = !enabled || overallFetching || (shouldFetchHomeAway && (homeFetching || awayFetching));

    return (
        <div
            data-tournament-overview-loaded={
                !summaryLoading && !homeAwayLoading && !isTournamentsLoading ? "true" : undefined
            }
            className="flex flex-col gap-4 pb-4"
        >
            <div id="onboarding-tournament-metrics">
                <TournamentAnalysisMetrics data={summaryData} isLoading={summaryLoading} />
            </div>
            <div id="onboarding-tournament-performance" className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                <div className="xl:col-span-8 flex flex-col gap-1">
                    <div className="px-4 pt-3 pb-1 min-h-11 flex items-center justify-between">
                        <h3 className="text-sm font-headline font-bold section-title">Standings & Performance</h3>
                        <div className="hidden md:flex items-center gap-2">
                            {homeAwayActiveTab === "standings" && !isPlayoffTournament && (
                                <div className="flex rounded-md p-0.5 gap-0.5 data-table-wrapper">
                                    {(["overall", "home", "away"] as const).map((tab) => (
                                        <button
                                            key={tab}
                                            type="button"
                                            onClick={() => setStandingsSubTab(tab)}
                                            className={`px-3 py-1 text-[10px] font-headline font-black uppercase tracking-widest rounded transition-all duration-150 cursor-pointer ${
                                                standingsSubTab === tab
                                                    ? "bg-white text-primary shadow-sm"
                                                    : "text-on-surface-variant hover:text-on-surface"
                                            }`}
                                        >
                                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="flex rounded-md p-0.5 gap-0.5 data-table-wrapper">
                                {(["standings", "home-away"] as const).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setHomeAwayActiveTab(tab)}
                                        className={`px-3 py-1 text-[10px] font-headline font-black uppercase tracking-widest rounded transition-all duration-150 cursor-pointer ${
                                            homeAwayActiveTab === tab
                                                ? "bg-white text-primary shadow-sm"
                                                : "text-on-surface-variant hover:text-on-surface"
                                        }`}
                                    >
                                        {tab === "standings" ? "Standings" : "Performance"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <TournamentAnalysisHomeAwayPerformance
                        activeTab={homeAwayActiveTab}
                        onActiveTabChange={setHomeAwayActiveTab}
                        standingsSubTab={standingsSubTab}
                        onStandingsSubTabChange={setStandingsSubTab}
                        homeAwayData={homeAwayData}
                        homeAwayLoading={homeAwayLoading}
                        overallStandings={overallStandings}
                        homeStandings={homeStandings}
                        awayStandings={awayStandings}
                        standingsLoading={standingsLoading}
                        isPlayoffTournament={!!isPlayoffTournament}
                    />
                </div>
                <div className="xl:col-span-4 flex flex-col gap-1">
                    <div className="flex items-center px-4 pt-3 pb-1 min-h-11">
                        <h3 className="text-sm font-headline font-bold section-title">Games Summary</h3>
                    </div>
                    <TournamentAnalysisGamesSummary
                        matchSummary={summaryData?.matchSummary}
                        isLoading={summaryLoading}
                    />
                </div>
            </div>
            <div id="onboarding-tournament-cards">
                <TournamentsAnalysisCards
                    tournamentId={selectedTournamentId}
                    topPlayers={topPlayersData}
                    topPlayersLoading={topPlayersLoading}
                    matchesData={matchesData}
                    matchesLoading={matchesLoading}
                    teamStats={homeAwayData?.teamStats}
                    teamStatsLoading={homeAwayLoading}
                    quickStatsData={quickStatsData}
                    quickStatsLoading={quickStatsLoading}
                />
            </div>
            <div id="onboarding-tournament-chart" className="h-108 flex flex-col gap-1">
                <h3 className="px-4 pt-3 pb-1 text-sm font-headline font-bold section-title shrink-0">
                    Match-by-Match Performance Trends
                    {chartSubTitle.sub && (
                        <>
                            <span className="text-on-surface-variant font-normal"> • </span>
                            <span style={{ color: chartSubTitle.subColor }}>{chartSubTitle.sub}</span>
                        </>
                    )}
                </h3>
                <div className="flex-1 min-h-0 rounded-xl overflow-hidden bg-surface-container-lowest">
                    <TournamentAnalysisChart
                        tournamentData={selectedTournamentData}
                        matchXgs={matchXgs}
                        isLoading={isTournamentsLoading}
                        tournamentId={selectedTournamentId}
                        onTitleChange={handleChartTitleChange}
                    />
                </div>
            </div>
            <div>
                <TournamentAnalysisAchievements
                    summaryData={summaryData}
                    homeAwayData={homeAwayData}
                    topPlayers={topPlayersData}
                    isLoading={summaryLoading || homeAwayLoading || topPlayersLoading}
                />
            </div>
        </div>
    );
};
